import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link } from 'wouter';
import { germanyPath } from '@/lib/markets';
import { useMarket } from '@/lib/market-context';
import { INTERNAL_HOSTS, normalizeRoute } from '@/lib/content';

export type ResolvedHref =
  | { kind: 'internal'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'protocol'; href: string }
  | { kind: 'none' };

/** Decide how a source URL should be linked. Never rewrites link text. */
export function resolveHref(url: string | null | undefined): ResolvedHref {
  if (!url) return { kind: 'none' };
  const raw = url.trim();
  if (!raw) return { kind: 'none' };
  if (/^(mailto:|tel:|sms:|whatsapp:)/i.test(raw)) return { kind: 'protocol', href: raw };
  if (raw.startsWith('#')) return { kind: 'protocol', href: raw };

  const abs = /^https?:\/\/([^/?#]+)([^?#]*)(.*)$/i.exec(raw);
  if (abs) {
    if (!INTERNAL_HOSTS.has(abs[1].toLowerCase())) return { kind: 'external', href: raw };
    const path = normalizeRoute(abs[2] || '/');
    return { kind: 'internal', href: path + (abs[3] || '') };
  }
  if (raw.startsWith('/')) {
    const [pathname, ...rest] = raw.split(/(?=[?#])/);
    return { kind: 'internal', href: normalizeRoute(pathname) + rest.join('') };
  }
  return { kind: 'external', href: raw };
}

type Props = AnchorHTMLAttributes<HTMLAnchorElement> & {
  to: string | null | undefined;
  children: ReactNode;
};

/** One anchor that knows whether to route client-side or leave the site. */
export default function SmartLink({ to, children, ...rest }: Props) {
  const { germany } = useMarket();
  const resolved = resolveHref(to);
  if (resolved.kind === 'none') {
    return <span {...(rest as object)}>{children}</span>;
  }
  if (resolved.kind === 'internal') {
    return (
      <Link href={germany ? germanyPath(resolved.href) : resolved.href} {...rest}>
        {germany && resolved.href.includes('/bgm-zahlen-schweiz') && typeof children === 'string' ? children.replace(/Schweiz/g, 'Deutschland') : children}
      </Link>
    );
  }
  const external = resolved.kind === 'external';
  return (
    <a
      href={resolved.href}
      {...(external ? { rel: 'noopener noreferrer' } : null)}
      {...rest}
    >
      {children}
    </a>
  );
}
